import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import reportService, {
  RevenueReport,
  JobsReport,
  UsersReport,
} from '../services/api/reportService';

type ReportType = 'revenue' | 'jobs' | 'users';
type DateRange = 'week' | 'month' | 'year';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(value);

export default function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>('revenue');
  const [dateRange, setDateRange] = useState<DateRange>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [revenueReport, setRevenueReport] = useState<RevenueReport | null>(null);
  const [jobsReport, setJobsReport] = useState<JobsReport | null>(null);
  const [usersReport, setUsersReport] = useState<UsersReport | null>(null);

  useEffect(() => {
    loadReport();
  }, [reportType, dateRange]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      if (reportType === 'revenue') {
        const data = await reportService.getRevenueReport(dateRange);
        setRevenueReport(data);
      } else if (reportType === 'jobs') {
        const data = await reportService.getJobsReport(dateRange);
        setJobsReport(data);
      } else {
        const data = await reportService.getUsersReport(dateRange);
        setUsersReport(data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load report');
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const now = new Date();
    const exportedAt = now.toLocaleString('en-NG', { dateStyle: 'full', timeStyle: 'short' });
    const rangeLabel = dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 30 Days' : 'Last 12 Months';
    const ngn = (v: number) => `NGN ${v.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const pct = (v: number) => `${v.toFixed(1)}%`;
    const sep = () => '';

    let rows: string[] = [];
    let filename = '';

    if (reportType === 'revenue' && revenueReport) {
      const s = revenueReport.summary;
      const commissionRate = s.total_revenue > 0
        ? pct((s.total_commission / s.total_revenue) * 100)
        : '0.0%';

      filename = `Daily2K_Revenue_Report_${dateRange}_${now.toISOString().slice(0, 10)}.csv`;
      rows = [
        // ── Header block ──
        'DAILY2K ADMIN — REVENUE REPORT',
        `Period:,${rangeLabel}`,
        `Exported:,${exportedAt}`,
        `Generated by:,Daily2K Admin Panel`,
        sep(),
        // ── Summary block ──
        '== SUMMARY ==================================',
        `Total Revenue,${ngn(s.total_revenue)}`,
        `Total Commission (Platform),${ngn(s.total_commission)}`,
        `Artisan Payouts,${ngn(s.total_revenue - s.total_commission)}`,
        `Commission Rate,${commissionRate}`,
        `Daily Average Revenue,${ngn(s.avg_per_day)}`,
        sep(),
        // ── Detail block ──
        '== DAILY BREAKDOWN ==========================',
        'Date,Revenue (NGN),Commission (NGN),Artisan Payout (NGN),Commission %',
        ...revenueReport.chart.map(p => {
          const payout = p.revenue - p.commission;
          const rate = p.revenue > 0 ? pct((p.commission / p.revenue) * 100) : '0.0%';
          return `${p.date},${p.revenue.toFixed(2)},${p.commission.toFixed(2)},${payout.toFixed(2)},${rate}`;
        }),
        sep(),
        // ── Footer ──
        `TOTAL,${s.total_revenue.toFixed(2)},${s.total_commission.toFixed(2)},${(s.total_revenue - s.total_commission).toFixed(2)},${commissionRate}`,
        sep(),
        'Confidential — Daily2K Internal Use Only',
      ];

    } else if (reportType === 'jobs' && jobsReport) {
      const s = jobsReport.summary;

      filename = `Daily2K_Jobs_Report_${dateRange}_${now.toISOString().slice(0, 10)}.csv`;
      rows = [
        'DAILY2K ADMIN — JOBS REPORT',
        `Period:,${rangeLabel}`,
        `Exported:,${exportedAt}`,
        `Generated by:,Daily2K Admin Panel`,
        sep(),
        '== SUMMARY ==================================',
        `Total Jobs Created,${s.total_created}`,
        `Total Completed,${s.total_completed}`,
        `Total Cancelled,${s.total_cancelled}`,
        `In Progress / Other,${s.total_created - s.total_completed - s.total_cancelled}`,
        `Completion Rate,${pct(s.completion_rate)}`,
        sep(),
        '== DAILY BREAKDOWN ==========================',
        'Date,Created,Completed,Cancelled,Completion Rate',
        ...jobsReport.chart.map(p => {
          const rate = p.created > 0 ? pct((p.completed / p.created) * 100) : '0.0%';
          return `${p.date},${p.created},${p.completed},${p.cancelled},${rate}`;
        }),
        sep(),
        `TOTAL,${s.total_created},${s.total_completed},${s.total_cancelled},${pct(s.completion_rate)}`,
        sep(),
        'Confidential — Daily2K Internal Use Only',
      ];

    } else if (reportType === 'users' && usersReport) {
      const s = usersReport.summary;
      const artisanShare = s.total_new > 0 ? pct((s.total_artisans / s.total_new) * 100) : '0.0%';
      const clientShare  = s.total_new > 0 ? pct((s.total_clients  / s.total_new) * 100) : '0.0%';

      filename = `Daily2K_Users_Report_${dateRange}_${now.toISOString().slice(0, 10)}.csv`;
      rows = [
        'DAILY2K ADMIN — USERS REPORT',
        `Period:,${rangeLabel}`,
        `Exported:,${exportedAt}`,
        `Generated by:,Daily2K Admin Panel`,
        sep(),
        '== SUMMARY ==================================',
        `All-Time Users,${s.total_all_time}`,
        `New Registrations (Period),${s.total_new}`,
        `New Artisans,${s.total_artisans} (${artisanShare} of new)`,
        `New Clients,${s.total_clients} (${clientShare} of new)`,
        sep(),
        '== DAILY BREAKDOWN ==========================',
        'Date,New Users,New Artisans,New Clients,Artisan %,Client %',
        ...usersReport.chart.map(p => {
          const aRate = p.total > 0 ? pct((p.artisans / p.total) * 100) : '0.0%';
          const cRate = p.total > 0 ? pct((p.clients  / p.total) * 100) : '0.0%';
          return `${p.date},${p.total},${p.artisans},${p.clients},${aRate},${cRate}`;
        }),
        sep(),
        `TOTAL,${s.total_new},${s.total_artisans},${s.total_clients},${artisanShare},${clientShare}`,
        sep(),
        'Confidential — Daily2K Internal Use Only',
      ];
    }

    if (!rows.length) return;
    // '﻿' is the UTF-8 BOM — Excel on Windows needs it to auto-detect UTF-8
    const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Reports & Analytics
      </Typography>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Report Type
              </Typography>
              <ToggleButtonGroup
                value={reportType}
                exclusive
                onChange={(_, v) => { if (v) setReportType(v); }}
                fullWidth
                size="small"
              >
                <ToggleButton value="revenue">Revenue</ToggleButton>
                <ToggleButton value="jobs">Jobs</ToggleButton>
                <ToggleButton value="users">Users</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Date Range
              </Typography>
              <ToggleButtonGroup
                value={dateRange}
                exclusive
                onChange={(_, v) => { if (v) setDateRange(v); }}
                fullWidth
                size="small"
              >
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1, justifyContent: { sm: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadReport}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={loading}
                size="small"
              >
                Export CSV
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Revenue Report */}
          {reportType === 'revenue' && revenueReport && (
            <Grid container spacing={3}>
              {/* Summary Cards */}
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'primary.main' }}>
                    {formatCurrency(revenueReport.summary.total_revenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg {formatCurrency(revenueReport.summary.avg_per_day)}/day
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total Commission</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'success.main' }}>
                    {formatCurrency(revenueReport.summary.total_commission)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Platform earnings</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Commission Rate</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                    {revenueReport.summary.total_revenue > 0
                      ? `${((revenueReport.summary.total_commission / revenueReport.summary.total_revenue) * 100).toFixed(1)}%`
                      : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Of total revenue</Typography>
                </Card>
              </Grid>

              {/* Chart */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Revenue vs Commission"
                    subheader={`${dateRange === 'week' ? 'Last 7 days' : dateRange === 'month' ? 'Last 30 days' : 'Last 12 months'}`}
                  />
                  <Divider />
                  <CardContent>
                    {revenueReport.chart.every(p => p.revenue === 0) ? (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography color="text.secondary">No revenue data for this period</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={revenueReport.chart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="revenue" fill="#2563eb" name="Revenue" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="commission" fill="#10b981" name="Commission" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Jobs Report */}
          {reportType === 'jobs' && jobsReport && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Jobs Created</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                    {jobsReport.summary.total_created}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'success.main' }}>
                    {jobsReport.summary.total_completed}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Cancelled</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'error.main' }}>
                    {jobsReport.summary.total_cancelled}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Completion Rate</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'primary.main' }}>
                    {jobsReport.summary.completion_rate}%
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Job Activity"
                    subheader={`${dateRange === 'week' ? 'Last 7 days' : dateRange === 'month' ? 'Last 30 days' : 'Last 12 months'}`}
                  />
                  <Divider />
                  <CardContent>
                    {jobsReport.summary.total_created === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography color="text.secondary">No job activity for this period</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={jobsReport.chart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="created" stroke="#2563eb" name="Created" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="completed" stroke="#10b981" name="Completed" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="cancelled" stroke="#ef4444" name="Cancelled" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Users Report */}
          {reportType === 'users' && usersReport && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">All-Time Users</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                    {usersReport.summary.total_all_time}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">New This Period</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'primary.main' }}>
                    {usersReport.summary.total_new}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">New Artisans</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'success.main' }}>
                    {usersReport.summary.total_artisans}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">New Clients</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: 'secondary.main' }}>
                    {usersReport.summary.total_clients}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="User Registrations"
                    subheader={`${dateRange === 'week' ? 'Last 7 days' : dateRange === 'month' ? 'Last 30 days' : 'Last 12 months'}`}
                  />
                  <Divider />
                  <CardContent>
                    {usersReport.summary.total_new === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography color="text.secondary">No new user registrations for this period</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={usersReport.chart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="artisans" fill="#10b981" name="Artisans" radius={[3, 3, 0, 0]} stackId="a" />
                          <Bar dataKey="clients" fill="#2563eb" name="Clients" radius={[3, 3, 0, 0]} stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
